// What changed between two creative revisions, field by field, in the order
// the drawer shows the fields — so the versions timeline reads the same way
// the editor does.

import type { CreativeAsset, CreativeRevisionSnapshot } from '@/api/types';
import { CHANNEL_FIELDS, isCreativeChannel, metaFieldsFromCreative } from '@/screens/creative-config';
import { diffWords, type DiffToken } from './text-diff';

export interface RevisionFieldRow {
  key: string;
  label: string;
  before: string;
  after: string;
  changed: boolean;
  tokens: DiffToken[];
}

interface FieldDescriptor {
  key: string;
  label: string;
}

type Shape = Pick<CreativeRevisionSnapshot, 'spec' | 'destination_url'>;

function fieldsFor(channel: string): FieldDescriptor[] {
  const descriptors: readonly FieldDescriptor[] = isCreativeChannel(channel) ? CHANNEL_FIELDS[channel] : [];
  return descriptors.filter((field) => field.key !== 'ad_name');
}

function valuesFor(channel: string, shape: Shape): Record<string, string> {
  if (channel === 'meta') {
    const parsed = metaFieldsFromCreative(shape) as Record<string, unknown>;
    return Object.fromEntries(
      Object.entries(parsed).map(([key, value]) => [key, typeof value === 'string' ? value : '']),
    );
  }
  return { dest_url: shape.destination_url };
}

/**
 * One row per content field, `changed` where the two shapes differ. A legacy
 * spec that was never JSON yields blank values rather than throwing, so a
 * timeline can still render it.
 */
export function revisionFieldRows(
  channel: string,
  previous: Shape | null,
  next: Shape,
): RevisionFieldRow[] {
  const after = valuesFor(channel, next);
  const before = previous ? valuesFor(channel, previous) : {};
  return fieldsFor(channel).map((field) => {
    const a = before[field.key] ?? '';
    const b = after[field.key] ?? '';
    const changed = previous !== null && a !== b;
    return {
      key: field.key,
      label: field.label,
      before: a,
      after: b,
      changed,
      tokens: changed ? diffWords(a, b) : [],
    };
  });
}

export type AssetChange = { placement_tag: string; kind: 'added' | 'removed' | 'replaced' };

export { placementLabel } from './creative-placement-labels';

function byTag(assets: CreativeAsset[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const asset of assets) {
    for (const tag of asset.placement_tags) out.set(tag, asset.asset_id);
  }
  return out;
}

/** Per placement tag: which asset serves it now versus before. */
export function assetDelta(previous: CreativeAsset[], next: CreativeAsset[]): AssetChange[] {
  const before = byTag(previous);
  const after = byTag(next);
  const tags = [...new Set([...before.keys(), ...after.keys()])].sort();
  const out: AssetChange[] = [];
  for (const tag of tags) {
    const a = before.get(tag);
    const b = after.get(tag);
    if (a === b) continue;
    out.push({ placement_tag: tag, kind: !a ? 'added' : !b ? 'removed' : 'replaced' });
  }
  return out;
}

const THUMB_PREFERENCE = ['meta_sq', 'meta_fs', 'meta_story', 'ctv_16x9'];

/** The asset a card should show: the squarest Meta ratio, else the first. */
export function primaryRevisionAsset(assets: CreativeAsset[]): CreativeAsset | null {
  for (const tag of THUMB_PREFERENCE) {
    const hit = assets.find((asset) => asset.placement_tags.includes(tag));
    if (hit) return hit;
  }
  return assets[0] ?? null;
}
